import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PRODUCT_MENU, FEATURE_MENU } from "@/data/catalog";
import { SectionHeading, WhyWhatsApp, IncludedStrip } from "@/components/Sections";
import FinalCta from "@/components/FinalCta";
import { useSeo } from "@/lib/seo";
import Breadcrumbs from "@/components/Breadcrumbs";

function Group({ id, eyebrow, title, sub, items, tinted }) {
  return (
    <section id={id} className={`py-20 md:py-24 scroll-mt-20 ${tinted ? "bg-slate-50" : ""}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading eyebrow={eyebrow} title={title} sub={sub} />
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((o) => (
            <Link key={o.slug} to={`/products/${o.slug}`} className="group min-w-0 bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#25D366] hover:shadow-md transition-all" data-testid={`features-${o.slug}`}>
              <span className="w-11 h-11 rounded-xl grid place-items-center bg-[#25D366]/10 text-[#25D366]"><o.icon className="w-5 h-5" /></span>
              <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{o.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{o.short}</p>
              {o.steps && (
                <ul className="mt-4 space-y-1.5">
                  {o.steps.map(([t]) => <li key={t} className="text-xs text-slate-500">• {t}</li>)}
                </ul>
              )}
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#25D366]">Learn more <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function FeaturesPage() {
  useSeo({
    title: "WhatsApp Marketing & Automation Features | GrabNChat",
    description: "Every GrabNChat feature: WhatsApp Business API, bulk campaigns, chatbots, shared inbox, CRM, automation, calling, catalogs, lead ads, Google reviews and social tools.",
  });
  return (
    <div data-testid="features-page">
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 to-white">
        <div className="absolute inset-0 bg-dots opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 md:pt-10"><Breadcrumbs items={[{ name: "Features", path: "/features" }]} /></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-16 pb-16 text-center">
          <div className="text-xs uppercase tracking-widest text-[#25D366] font-semibold mb-4">Features</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">WhatsApp Marketing, Automation &amp; Support Features</h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto">
            {PRODUCT_MENU.length + FEATURE_MENU.length} products and features for marketing, sales, support, reviews and social media — all in one GrabNChat account.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            <a href="#core" className="px-4 py-2 rounded-full border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:border-[#25D366]">WhatsApp platform</a>
            <a href="#growth" className="px-4 py-2 rounded-full border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:border-[#25D366]">Growth & marketing</a>
          </div>
        </div>
      </section>
      <Group id="core" eyebrow="WhatsApp platform" title="Core WhatsApp products" sub="Messaging, automation, support and commerce on the official WhatsApp Business API." items={PRODUCT_MENU} />
      <IncludedStrip />
      <Group id="growth" tinted eyebrow="Growth & marketing" title="Features that bring in more customers" sub="Ads, leads, follow-ups, Google reviews, social media and AI content." items={FEATURE_MENU} />
      <WhyWhatsApp />
      <FinalCta />
    </div>
  );
}
